<template>
  <!--
    Persistent top navigation (Requirement 1.4 - rendered in the app shell on
    every page). The visible menu is derived entirely from the pure
    `resolveNavItems` helper so enabling a hidden section is a configuration
    change only (Requirements 1.2, 2.3).
  -->
  <v-app-bar color="surface" elevation="2" class="navigation-bar" flat>
    <!-- Logo links to the Home page (Requirement 1.1). -->
    <router-link
      to="/"
      class="brand-link"
      aria-label="ThinkSpatial home"
    >
      <img :src="logo" alt="ThinkSpatial" class="brand-logo" />
    </router-link>

    <v-spacer />

    <!--
      At or above the mobile breakpoint, present the full Primary_Menu
      (Requirement 1.5).
    -->
    <nav v-if="!mobile" class="primary-nav" aria-label="Primary">
      <v-btn
        v-for="item in primary"
        :key="item.id"
        :to="item.to"
        :active="isItemActive(item)"
        variant="text"
        class="nav-link"
      >{{ item.label }}</v-btn>

      <!--
        Top-right account entry. When signed in, link to the Admin area and show
        the user; otherwise show the flag-gated Login entry
        (Requirements 3.2, 3.3).
      -->
      <v-btn
        v-if="isAuthenticated"
        :to="adminItem.to"
        :active="isItemActive(adminItem)"
        variant="outlined"
        color="primary"
        class="nav-link login-btn"
        prepend-icon="mdi-account"
      >{{ accountLabel }}</v-btn>
      <v-btn
        v-else-if="showLogin"
        to="/login"
        :active="isItemActive(loginItem)"
        variant="outlined"
        color="primary"
        class="nav-link login-btn"
        prepend-icon="mdi-account"
      >Login</v-btn>
    </nav>

    <!--
      Strictly below the mobile breakpoint, collapse the Primary_Menu into a
      hamburger control that opens the drawer (Requirement 1.5).
    -->
    <v-app-bar-nav-icon
      v-else
      class="nav-toggle"
      aria-label="Open navigation menu"
      @click="drawer = !drawer"
    />
  </v-app-bar>

  <!-- Collapsible mobile menu (Requirement 1.5). -->
  <v-navigation-drawer
    v-if="mobile"
    v-model="drawer"
    temporary
    location="right"
    class="mobile-nav"
  >
    <v-list nav aria-label="Primary">
      <v-list-item
        v-for="item in primary"
        :key="item.id"
        :to="item.to"
        :active="isItemActive(item)"
        :title="item.label"
        class="nav-link"
      />

      <template v-if="isAuthenticated">
        <v-divider class="my-2" />
        <v-list-item
          :to="adminItem.to"
          :active="isItemActive(adminItem)"
          :title="accountLabel"
          prepend-icon="mdi-account"
          class="nav-link login-btn"
        />
      </template>
      <template v-else-if="showLogin">
        <v-divider class="my-2" />
        <v-list-item
          to="/login"
          :active="isItemActive(loginItem)"
          title="Login"
          prepend-icon="mdi-account"
          class="nav-link login-btn"
        />
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup>
// NavigationBar - Vue 3 + Vuetify 3 implementation.
//
// Responsibilities (design "Components and Interfaces > NavigationBar"):
//   - Render the ThinkSpatial logo linking to Home (Req 1.1).
//   - Render the Primary_Menu computed by `resolveNavItems(features)` - Home,
//     About Us, Projects, Contact Us, plus Software when its flag is on
//     (Reqs 1.2, 2.3).
//   - Render the top-right Login entry only when the Login flag is on
//     (Reqs 3.2, 3.3).
//   - Indicate the active Primary_Menu item (Req 1.6).
//   - Collapse to a hamburger/drawer below the mobile breakpoint and show the
//     full menu at/above it (Req 1.5).
//   - Expose every link to keyboard operation with visible focus (Req 9.5).
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';

import { runtimeConfig } from 'src/config/runtimeConfig';
import { resolveNavItems } from 'src/config/resolveNavItems';
import { useAuthStore } from 'src/stores/auth';
// SVGs are loaded as URL assets (webpack 5 asset/resource) and rendered via
// <img>; no new logo artwork is introduced (Req 8.3).
import logo from 'assets/svg/ts-logo.svg';

const route = useRoute();
// `mobile` is true strictly below Vuetify's mobile breakpoint (Req 1.5).
const { mobile } = useDisplay();

const drawer = ref(false);

// Visible navigation is derived from the feature flags via the pure helper.
const nav = computed(() => resolveNavItems(runtimeConfig.features));
const primary = computed(() => nav.value.primary);
const showLogin = computed(() => nav.value.showLogin);

// The Login entry is not part of the primary menu; describe it locally so the
// active-state helper can treat it uniformly.
const loginItem = { id: 'login', label: 'Login', name: 'login', to: '/login' };

// Auth-aware top-right entry. When a session is active the entry links to the
// Admin area (rather than showing "Login", which the Auth_Guard would only
// bounce to /admin anyway) and reflects the signed-in user. The Admin entry is
// treated as active for any /admin* route so it highlights while managing
// content.
const auth = useAuthStore();
const isAuthenticated = computed(() => auth.isAuthenticated);
const adminItem = { id: 'admin', label: 'Admin', name: 'admin', to: '/admin' };
const accountLabel = computed(() => {
  const profile = auth.profile || {};
  return (
    (profile.first_name && String(profile.first_name)) ||
    (profile.email && String(profile.email)) ||
    'Admin'
  );
});

/**
 * Determine whether a nav item is the active one (Requirement 1.6).
 *
 * Home (`/`) is active only on an exact match; every other item is active when
 * the current route equals it or is nested beneath it. This guarantees a single
 * active indicator rather than Home matching every route as a path prefix.
 *
 * @param {{ to: string }} item
 * @returns {boolean}
 */
function isItemActive(item) {
  if (item.to === '/') {
    return route.path === '/';
  }
  return route.path === item.to || route.path.startsWith(`${item.to}/`);
}
</script>

<style scoped>
.brand-link {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  text-decoration: none;
  border-radius: 4px;
}

.brand-logo {
  height: 36px;
  width: auto;
  display: block;
}

.primary-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 8px;
}

.login-btn {
  margin-left: 8px;
}

/*
  Visible focus indication for keyboard users on every interactive element
  (Requirement 9.5). Uses the brand accent for a clearly visible ring.
*/
.brand-link:focus-visible,
.nav-link:focus-visible,
.nav-toggle:focus-visible,
:deep(.nav-link:focus-visible) {
  outline: 3px solid #e7511e;
  outline-offset: 2px;
}
</style>
